import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  APPLICANT_STAGE_LABELS,
  APPLICANT_STAGES,
  REFERRAL_DUPLICATE_FILTER_LABELS,
  REFERRAL_DUPLICATE_FILTERS,
  REFERRAL_STATUS_LABELS,
  REVIEWABLE_REFERRAL_STATUSES,
  type ReferralListItem,
} from '@hv/shared';
import { useCycles, useReferralFilterOptions, useReferralList, type ReferralListParams } from '../api/hooks';
import { useAuth } from '../components/AuthContext';
import { describeError } from '../components/Notify';
import { ReferralStatusControl } from '../referrals/ReferralStatusControl';
import { REFERRAL_FILTER_KEYS, referralFilterParams, referralFiltersFromParams } from '../referrals/filters';
import { ReferralExportDialog } from '../referrals/ReferralExport';
import { useUrlState } from '../urlState';

const PAGE_SIZES = [25, 50, 100];

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');

function useDebouncedParam(key: string) {
  const { params, set } = useUrlState();
  const url = params.get(key) ?? '';
  const [value, setValue] = useState(url);
  useEffect(() => setValue(url), [url]);
  useEffect(() => {
    if (value.trim() === url) return;
    const t = setTimeout(() => set({ [key]: value.trim() || null, page: null }), 300);
    return () => clearTimeout(t);
  }, [value, url, key, set]);
  return [value, setValue] as const;
}

export function ReferralsPage() {
  const { params, set } = useUrlState();
  const cycles = useCycles();
  const options = useReferralFilterOptions();
  const [search, setSearch] = useDebouncedParam('search');
  const [school, setSchool] = useDebouncedParam('school');
  const [exporting, setExporting] = useState(false);
  const cycleId = params.get('cycle') ?? cycles.data?.find((c) => c.active)?.id;
  const pageSize = PAGE_SIZES.includes(Number(params.get('size'))) ? Number(params.get('size')) : 25;
  const page = Math.max(1, Number(params.get('page')) || 1);
  const filters = referralFiltersFromParams(params, cycleId);
  const query: ReferralListParams = { ...filters, page, pageSize };
  const list = useReferralList(query, !!cycleId || cycles.isFetched);
  const filtered = REFERRAL_FILTER_KEYS.some((k) => params.get(k));

  useEffect(() => {
    if (options.error) console.error('Loading referral filter options failed', options.error);
  }, [options.error]);

  const setFilter = (key: string, value: string) => set({ [key]: value || null, page: null });
  const select = (key: string, label: string, items: { value: string; label: string }[], width = 180) => (
    <TextField select size="small" label={label} value={params.get(key) ?? ''} onChange={(e) => setFilter(key, e.target.value)} sx={{ minWidth: width }}>
      <MenuItem value="">All</MenuItem>
      {items.map((i) => (
        <MenuItem key={i.value} value={i.value}>
          {i.label}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="baseline" gap={2} mb={2} flexWrap="wrap">
        <Typography variant="h2">Referrals</Typography>
        <Typography variant="body2" color="text.secondary" data-testid="referral-count">
          {list.data ? `${list.data.total} submitted referral${list.data.total === 1 ? '' : 's'} in this cycle` : ''}
        </Typography>
        <Button variant="outlined" size="small" startIcon={<FileDownloadIcon />} sx={{ ml: 'auto' }} disabled={!list.data} onClick={() => setExporting(true)}>
          Export
        </Button>
      </Stack>
      <Paper component="section" aria-label="Referral filters" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
          <TextField size="small" label="Search" placeholder="Reference, family, child or trustee" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 260 }} />
          {select('status', 'Status', REVIEWABLE_REFERRAL_STATUSES.map((s) => ({ value: s, label: REFERRAL_STATUS_LABELS[s] })))}
          {select('trustee', 'Trustee', (options.data?.trustees ?? []).map((t) => ({ value: t.id, label: t.displayName || t.email })), 200)}
          {select('grade', 'Grade', (options.data?.grades ?? []).map((g) => ({ value: g, label: g })), 120)}
          <TextField size="small" label="Current school" value={school} onChange={(e) => setSchool(e.target.value)} />
          {select('stage', 'Applicant stage', APPLICANT_STAGES.map((s) => ({ value: s, label: APPLICANT_STAGE_LABELS[s] })), 200)}
          {select('dup', 'Duplicates', REFERRAL_DUPLICATE_FILTERS.map((d) => ({ value: d, label: REFERRAL_DUPLICATE_FILTER_LABELS[d] })), 220)}
          <TextField size="small" type="date" label="Submitted from" value={params.get('from') ?? ''} onChange={(e) => setFilter('from', e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField size="small" type="date" label="Submitted to" value={params.get('to') ?? ''} onChange={(e) => setFilter('to', e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          {filtered && (
            <Button size="small" onClick={() => set({ ...Object.fromEntries(REFERRAL_FILTER_KEYS.map((k) => [k, null])), page: null })}>
              Clear filters
            </Button>
          )}
        </Stack>
      </Paper>
      {list.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load referrals: {describeError(list.error)}
        </Alert>
      )}
      <TableContainer component={Paper}>
        <Table size="small" aria-label="Referrals">
          <TableHead>
            <TableRow>
              <TableCell>Reference</TableCell>
              <TableCell>Family and children</TableCell>
              <TableCell>Trustee</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.data?.items.map((r) => <ReferralRow key={r.id} r={r} />)}
            {list.data?.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>{filtered ? 'No referrals match these filters.' : 'No submitted referrals in this cycle yet.'}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <ReferralExportDialog
        open={exporting}
        onClose={() => setExporting(false)}
        filters={filters}
        filterParams={referralFilterParams(params, cycleId)}
        total={list.data?.total ?? 0}
      />
      <TablePagination
        component="div"
        count={list.data?.total ?? 0}
        page={list.data && (page - 1) * pageSize < list.data.total ? page - 1 : 0}
        onPageChange={(_, p) => set({ page: p ? String(p + 1) : null })}
        rowsPerPage={pageSize}
        rowsPerPageOptions={PAGE_SIZES}
        onRowsPerPageChange={(e) => set({ size: e.target.value === '25' ? null : e.target.value, page: null })}
      />
    </Box>
  );
}

function ReferralRow({ r }: { r: ReferralListItem }) {
  const { can } = useAuth();
  const { set } = useUrlState();
  const open = () => r.householdId && set({ h: r.householdId, r: r.id }, false);
  return (
    <TableRow hover data-testid="referral-row">
      <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'ui-monospace, Menlo, monospace' }}>{r.referenceNumber}</TableCell>
      <TableCell>
        <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
          {r.householdId ? (
            <Link component="button" fontWeight={700} onClick={open} aria-label={`Open ${r.familyLastName} family`}>
              {r.familyLastName}
            </Link>
          ) : (
            <Typography fontWeight={700}>{r.familyLastName}</Typography>
          )}
          {r.countsTowardAllotment && <Chip size="small" variant="outlined" color="secondary" label="Recommendation" />}
          {r.openDuplicates > 0 && (
            <Tooltip title="Possible duplicate needs review">
              <WarningAmberIcon fontSize="small" color="warning" aria-label="Possible duplicate needs review" />
            </Tooltip>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {r.children.map((c) => [c.name, c.grade && `Gr ${c.grade}`, c.currentSchool].filter(Boolean).join(' · ')).join('; ')}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{r.trustee.displayName || r.trustee.email}</Typography>
        <Typography variant="caption" color="text.secondary">
          {r.trustee.email}
        </Typography>
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(r.submittedAt)}</TableCell>
      <TableCell>
        <ReferralStatusControl referral={r} canChange={can('referrals.review')} />
      </TableCell>
    </TableRow>
  );
}
